import './component.scss';

import { application } from 'src';
import { Controller } from '@hotwired/stimulus';
import { find, getParent, addClass, removeClass } from '@fretadao/f-js-dom';
import { isPresent, isObject, sortBy } from '@fretadao/f-js-utils';

import originImg from 'images/icons/map/origin.svg';
import iconImg from 'images/icons/map/marker-icon.png';
import blueIcon from 'images/icons/map/outgoing-icon.svg';
import blackIcon from 'images/icons/map/ingoing-icon.svg';
import iconRetinaImg from 'images/icons/map/marker-icon-2x.png';
import iconEndPointCompany from 'images/icons/map/marker-end-point.png';
import iconPassenger from 'images/icons/map/marker-passenger.svg';
import iconCheckedInPassenger from 'images/icons/map/marker-passenger-check-in.svg';
import iconBus from 'images/icons/map/marker-bus-online.png';
import blackCircle from 'images/ride_requests/map_circle.svg';
import solidBlueIcon from 'images/ride_requests/map_location.svg';

import axios from 'axios';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine';
import 'leaflet-routing-machine-here';
import 'leaflet.gridlayer.googlemutant';
import 'src/vendor/lrm-tomtom';
import 'leaflet-providers/leaflet-providers';
import 'leaflet.markercluster/dist/leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'polyline-encoded';
import 'packs/leaflet.forceZIndex';

import { API } from '../../frontend/src/api';

application.register('map', class extends Controller {
  static values = {
    origin: {
      type: Array,
      default: []
    },
    destination: {
      type: Array,
      default: []
    },
    pageMethod: {
      type: String,
      default: ''
    },
    agreementSlug: {
      type: String,
      default: ''
    },
    tripDetailId: {
      type: String,
      default: ''
    },
    direction: {
      type: String,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    },
    customHeight: {
      type: Boolean,
      default: false
    }
  };
  static targets = ['ingoingButton', 'outgoingButton', 'lineCard', 'mapEl', 'overlay', 'requestBaseCard'];
  debug = false;

  INIT_COORDINATES = L.latLng(-23.6821604, -46.8754915);
  INIT_ZOOM = 14;
  MIN_ZOOM = 10;
  MAX_ZOOM = 18;
  ACTIVE_LINE_CARD_CLASSES = ['border-blue-500'];
  ACTIVE_COLOR = '#0052CC';
  DISABLED_COLOR = '#323232';
  ACTIVE_LINE_Z = 20000;
  DISABLED_LINE_Z = 10000;

  lines = [];
  selectedLine = null;
  currentDirection = 'ingoing';

  map = null;
  mapEl = this.mapElTarget;
  mapOptions = JSON.parse(this.mapEl.dataset.mapOptions);

  passengersGroup = null;
  routingControls = {};
  drawableLayer = new L.featureGroup();

  passengersMarker = {};

  iconsList = {
    originImg, iconImg, blueIcon, blackIcon, iconRetinaImg, iconEndPointCompany,
    iconPassenger, iconCheckedInPassenger, iconBus, blackCircle, solidBlueIcon
  };

  async connect() {
    if (this.debug) console.log('async connect() {')
    this.#addOverlay();
    this.#updateSelectedButton();
    if (!this.customHeightValue) this.#setMapContainerHeight();
    this.#setMap();

    if (this.hasDirectionValue && this.directionValue) this.currentDirection = this.directionValue;

    // list map pages here
    switch (this.pageMethodValue) {
      case 'b2bLineMap':
        // /b2b/linhas/:id/map
        await this.b2bLineMapScreen();
        break;
      case 'b2bTripDetailMap':
        // /b2b/operacoes/:id/map
        await this.b2bTripDetailMapScreen();
        break;
      case 'addressForm':
        // /b2b/:slug/configuracoes/destinos
        this.latEl = find('#agreement_place_of_interest_address_attributes_latitude');
        this.longEl = find('#agreement_place_of_interest_address_attributes_longitude');
        this.#setFieldListeners();
        this.#setSizeInvalidationHook();
        break;
      case 'rideRequest':
        // /transporte-individual/vouchers/:code/viagens/novo
        this.rideRequestMapScreen();
        break;
      default:
        // /busca/visualizar-no-mapa
        await this.loadVisualizationScreen();
    }

    this.#removeOverlay();
  }

  handlePassengerClick() {
    if(this.debug) console.log('#handlePassengerClick() {');

    const passengerMarker = this.passengersMarker[event.currentTarget.dataset.passengerId];

    if(!passengerMarker) return;

    passengerMarker.togglePopup();
    this.map.setView(passengerMarker.getLatLng(), 15, { animation: true });
  }

  #setSizeInvalidationHook() {
    // to avoid the Leaflet's "gray map tiles" bug
    window.dispatchEvent(new Event('resize'));

    setInterval(() => {
      window.dispatchEvent(new Event('resize'));
    }, 3000);
  }

  // setting the latEl and longEl you can use as many times do you want for different fields
  // example of fields:
  // <input id="lat" data-marker-name="myMarker" data-marker-icon="iconBus" />
  // <input id="long" data-marker-name="myMarker" data-marker-icon="iconBus" />
  #setFieldListeners(latEl = this.latEl, longEl = this.longEl) {
    if (latEl) {
      latEl.addEventListener('change', ({ target }) => {
        this.#plotMarker(
          latEl, longEl, target.dataset.markerName, target.dataset.markerIcon
        );
      });
    }

    if (longEl) {
      longEl.addEventListener('change', ({ target }) => {
        this.#plotMarker(
          latEl, longEl, target.dataset.markerName, target.dataset.markerIcon
        );
      });
    }
  }

  // can receive the custom infos for lat/long or use the generic as this.addressLat/this.addressLong
  // name is optional, it used when have multiple markers
  // icon is optional, list of icons at iconsList var
  //
  // when move this marker on map, it will dispach two events
  // `geocomplete:markerMoved` for any marker moved
  // `geocomplete:markerMoved:marker_myMarker` just when it marker was moved
  #plotMarker(latEl = this.addressLat, longEl = this.addressLong, name = null, icon = null) {
    const lat = latEl.value;
    const lng = longEl.value;

    if (name) name = `marker_${name}`;

    if (lat && lng) {
      const addressMarkerLatLng = L.latLng(lat, lng);
      let markerOptions = {
        draggable: true,
        zIndex: 11000,
        name: name
      };

      if (icon) {
        const divIcon = new L.DivIcon({
          className: 'text-center drop-shadow-lg',
          iconSize: [30, 30],
          pane: 'markerBusPane',
          html: `<img style="margin-top: -20px; width: 100% !important" src="${this.iconsList[icon]}" />`
        });
        markerOptions.icon = divIcon;
      }

      if (name) {
        if (this.map[name]) this.map.removeLayer(this.map[name]);
      } else {
        if (this.map._addressMarker) this.map.removeLayer(this.map._addressMarker);
      }

      const addressMarker = L.marker(addressMarkerLatLng, markerOptions);

      addressMarker.on('dragend', (event) => {
        latEl.value = addressMarker.getLatLng().lat;
        longEl.value = addressMarker.getLatLng().lng;

        const evt = new CustomEvent('geocomplete:markerMoved', { detail: addressMarker.getLatLng() });
        this.mapEl.dispatchEvent(evt);

        const evtWithName = new CustomEvent(`geocomplete:markerMoved:${name}`, { detail: addressMarker.getLatLng() });
        this.mapEl.dispatchEvent(evtWithName);
      });

      if (name) {
        this.map[name] = addressMarker;
        this.map[name].addTo(this.map);
      } else {
        this.map._addressMarker = addressMarker;
        this.map._addressMarker.addTo(this.map);
      }

      this.map.setView(addressMarker._latlng, 20, { animation: true });
    } else {
      if (name) {
        if (this.map[name]) this.map.removeLayer(this.map[name]);
      } else {
        if (this.map._addressMarker) this.map.removeLayer(this.map._addressMarker);
      }
    }
  }

  disconnect() {
    if (this.debug) console.log('disconnect() {')
    this.map.eachLayer((layer) => layer.remove());
    this.map.remove();
    this.map = null;
  }

  rideRequestMapScreen() {
    const startLatEl = find('#ride_request_guest_trip_start_latitude');
    const startLongEl = find('#ride_request_guest_trip_start_longitude');

    const endLatEl = find('#ride_request_guest_trip_end_latitude');
    const endLongEl = find('#ride_request_guest_trip_end_longitude');

    const coordsFields = [startLatEl, startLongEl, endLatEl, endLongEl];
    const layerName = 'ride-request-trip';

    const clearMarkers = () => {
      this.routingControls[layerName]?.remove();
      this.map[`marker_${startLatEl.dataset.markerName}`]?.remove();
      this.map[`marker_${endLatEl.dataset.markerName}`]?.remove();
    }

    const focusButton = L.easyButton('fa-location-crosshairs', () => {
      let coords = [];

      if (this.routingControls[layerName]) {
        coords = this.routingControls[layerName].getWaypoints().map((waypoint) => waypoint.latLng);
      } else {
        const startCoords = [startLatEl.value, startLongEl.value];
        const endCoords = [endLatEl.value, endLongEl.value];

        coords = startCoords.every((coord) => coord) ? [startCoords] : [endCoords];
      }

      this.map.flyToBounds(coords);
    }, 'Focus on route');
    focusButton.setPosition('bottomleft');
    focusButton.button.style.position = 'absolute';
    focusButton.button.style.bottom = '15px';
    focusButton.button.style.backgroundColor = 'white';
    focusButton.button.style.padding = '8px';
    focusButton.button.style.fontSize = '16px';
    focusButton.button.style.border = '2px solid rgba(0,0,0,0.2)';
    focusButton.button.style.borderRadius = '5px';
    focusButton.addTo(this.map);

    const createMarker = (_itemIndex, options, _totalItem) => {
      const latLongObj = options.latLng;

      if (!latLongObj) return;

      const iconStyle = latLongObj.icon === 'blackCircle' ? '' : 'margin-top: -20px;';
      const divIcon = new L.DivIcon({
        className: 'text-center drop-shadow-lg',
        iconSize: [30, 30],
        pane: 'markerRideRequest',
        html: `<img style="${iconStyle} width: 100% !important" src="${this.iconsList[latLongObj.icon]}" />`
      });

      let markerOptions = {
        draggable: true,
        zIndex: 11000,
        name: latLongObj.name,
        icon: divIcon
      };

      const marker = this.createMarker(latLongObj, markerOptions);

      marker.on('dragend', () => {
        const evtWithName = new CustomEvent(`geocomplete:markerMoved:marker_${latLongObj.name}`, { detail: marker.getLatLng() });
        this.mapEl.dispatchEvent(evtWithName);
      });

      return marker;
    }

    coordsFields.forEach((field) => {
      field.addEventListener('change', () => {
        const startLatLong = L.latLng(startLatEl.value, startLongEl.value);
        startLatLong.name = startLatEl.dataset.markerName;
        startLatLong.icon = startLatEl.dataset.markerIcon;

        const endLatLong = L.latLng(endLatEl.value, endLongEl.value);
        endLatLong.name = endLatEl.dataset.markerName;
        endLatLong.icon = endLatEl.dataset.markerIcon;

        const markers = [startLatLong, endLatLong];
        const allCoordsFilled = coordsFields.every((coordsField) => isPresent(coordsField.value));

        clearMarkers();

        if (!allCoordsFilled) {
          let latEl = startLatEl;
          let longEl = startLongEl;

          if (endLatEl.value && endLongEl.value) {
            latEl = endLatEl;
            longEl = endLongEl;
          }

          this.#plotMarker(
            latEl, longEl, latEl.dataset.markerName, latEl.dataset.markerIcon
          );
        } else {
          this.addRoutingControl(layerName, markers, {
            createMarker: createMarker,
            routeWhileDragging: true,
            lineOptions: {
              styles: [
                {
                  color: 'white',
                  opacity: 0.8,
                  weight: 10,
                },
                {
                  color: this.ACTIVE_COLOR,
                  opacity: 1,
                  weight: 4,
                }
              ]
            },
          });

          this.routingControls[layerName].on('routesfound', (event) => {
            const route = event.routes[0];
            if (route) this.map.flyToBounds(route.coordinates);
          });
        }
      });
    });
  }

  async b2bLineMapScreen() {
    if (this.debug) console.log('b2bLineMapScreen () {')

    const lineId = this.requestBaseCardTarget.dataset.routeId;

    await this.#b2bFetchLine(lineId).then(this.#b2bMapCallback.bind(this));
  }

  async b2bTripDetailMapScreen() {
    if (this.debug) console.log('b2bTripDetailMapScreen () {')

    await this.#b2bTripDetailFetchMap().then(this.#b2bMapCallback.bind(this));
  }

  async #b2bMapCallback(result) {
    const line = result.data;

    this.lines.push(line);
    this.selectedLine = line;
    this.currentLine = line;

    if (this.pageMethodValue == 'b2bTripDetailMap') await this.#passengerCheckins();

    this.#createMarkerBasedOnDirection(line);
    this.createBusMarker();
  }

  async createBusMarker() {
    const busLocation = await this.getBusLocation();

    if (!busLocation.latitude) return;

    this.busMarker = L.marker([busLocation.latitude, busLocation.longitude], {
      draggable: false,
      contextmenu: true,
      contextmenuInheritItems: false,
      contextmenuItems: []
    });

    let icon = new L.DivIcon({
      className: 'text-center drop-shadow-lg',
      iconSize: [40, 40],
      pane: 'markerBusPane',
      html: `<img style="margin-top: -25px; margin-right: -25px; width: 100% !important" src="${iconBus}" />`
    });

    let popupMessage = null;

    if (busLocation.updated_at) {
      popupMessage = `Atualizado há ${busLocation.pretty_updated_at}`;
    } else {
      popupMessage = 'Não há atualizações recentes';
    }

    const popup = `<p style="font-size: 11px">${popupMessage}</p></br>`;

    const options = {
      pane: 'markerPane',
      draggable: false,
      icon: icon,
      zIndex: 11000,
      popup: popup
    };

    if (options.popup) this.busMarker.bindPopup(options.popup);
    if (options.icon) this.busMarker.options.icon = options.icon;
    if (options.alt) this.busMarker.options.alt = options.alt;
    if (options.zIndex) this.busMarker.options.forceZIndex = options.zIndex;
    if (options.hide !== undefined && options.hide) this.busMarker.options.opacity = 0;
    if (options.name) this.busMarker.options.name = options.name;

    this.busMarker.addTo(this.map);
    setInterval(() => this.updateBusMarker(), 90000);
  }

  async updateBusMarker() {
    const busLocation = await this.getBusLocation();

    this.busMarker.setLatLng([busLocation.latitude, busLocation.longitude]);
  }

  async getBusLocation() {
    if (this.debug) console.log('async getBusLocation() {');

    return axios.get(`/api/lines/${this.currentLine.id}/bus_status?direction=${this.currentDirection}`)
      .then((response) => response.data)
      .catch((response) => response);
  }

  async loadVisualizationScreen() {
    if (this.debug) console.log('async loadVisualizationScreen() {')

    const firstCard = this.lineCardTargets[0];
    await this.#getAllLines();
    this.selectedLine = this.findMatchingLine(firstCard.dataset.routeId);
    this.lines.forEach((line) => {
      this.#createMarkerBasedOnDirection(line);
    });

    await this.applyLine(this.selectedLine, { force: true });

    this.#applySelectedCardBorder(this.selectedLine.id);
  }

  findMatchingLine(lineId) {
    if (this.debug) console.log('findMatchingLine(lineId) {')

    return this.lines.find((line) => line.id == lineId) || {};
  }

  async selectLineFromCard({ target }) {
    if (this.debug) console.log('async selectLineFromCard({ target }) {')

    const lineId = this.#searchCardForRouteId(target);
    await this.applyLine(this.findMatchingLine(lineId));
  }

  async applyLine(line, options = { force: false }) {
    if (this.debug) console.log('async applyLine(line, force = false) {')
    if ((this.selectedLine.id == line.id) && !options.force) return;

    const oldSelectedLine = this.selectedLine;
    const newSelectedLine = line;

    this.selectedLine = newSelectedLine;

    if (this.readonlyValue) {
      const oldLineLayer = this.map._lineLayers[`${oldSelectedLine.id}-${this.currentDirection}`];
      const newLineLayer = this.map._lineLayers[`${newSelectedLine.id}-${this.currentDirection}`];

      this.map.removeLayer(oldLineLayer);
      this.map.removeLayer(newLineLayer);

      [oldSelectedLine, newSelectedLine].forEach((line) => {
        this.#renderRouteForLine(line, {
          lineColor: line.id == this.selectedLine.id ? this.ACTIVE_COLOR : this.DISABLED_COLOR,
          zIndex: line.id == this.selectedLine.id ? this.ACTIVE_LINE_Z : this.DISABLED_LINE_Z,
          force: true,
          markerOptions: {
            lineId: line.id,
            createMarkerFunction: this.#createSearchMarker.bind(this)
          }
        }, this.currentDirection);
      });
    } else {
      if (oldSelectedLine != undefined) await this.#refreshLine(oldSelectedLine);

      await this.#refreshLine(this.selectedLine);
    }

    this.#addOverlay();
    this.#applySelectedCardBorder(this.selectedLine.id);
    this.#removeOverlay();
  };

  async selectDirection(event) {
    if (this.debug) console.log('async selectDirection(event) {')

    this.oldDirection = this.currentDirection;
    this.currentDirection = event.params.direction;

    this.#updateSelectedButton();
    this.#addOverlay();

    if (this.readonlyValue) {
      this.map.removeLayer(this.map._lineLayers[`${this.currentLine.id}-${this.oldDirection}`]);
      this.#renderRouteForLine(this.currentLine, { lineColor: this.ACTIVE_COLOR }, this.currentDirection);
    } else {
      for (let i = 0; i < this.lines.length; i++) {
        const lineID = this.lines[i].id;
        const control = this.#getRoutingControl(lineID, this.oldDirection);

        control.setWaypoints([]);
      }

      for (let i = 0; i < this.lines.length; i++) {
        await this.#refreshLine(this.lines[i])
      }

      if (this.selectedLine) await this.applyLine(this.selectedLine, true);
    }

    this.#removeOverlay()
  }

  async selectDirectionMultiLines(event) {
    if (this.debug) console.log('async selectDirectionMultiLines(event) {')

    if (this.readonlyValue) {
      this.oldDirection = this.currentDirection;
      this.currentDirection = event.params.direction;

      this.#updateSelectedButton();
      this.#addOverlay();

      this.lines.forEach((line) => {
        const oldLayer = `${line.id}-${this.oldDirection}`;

        if (this.map._lineLayers[oldLayer]) this.map.removeLayer(this.map._lineLayers[oldLayer]);

        this.#renderRouteForLine(line, {
          lineColor: line.id == this.selectedLine.id ? this.ACTIVE_COLOR : this.DISABLED_COLOR,
          zIndex: line.id == this.selectedLine.id ? this.ACTIVE_LINE_Z : this.DISABLED_LINE_Z,
          markerOptions: {
            lineId: line.id,
            createMarkerFunction: this.#createSearchMarker.bind(this)
          }
        }, this.currentDirection);
      });

      this.#removeOverlay();
    } else {
      await this.selectDirection(event);
    }
  }

  clearAllPolylineLayers() {
    Object.values(this.map._lineLayers).forEach((layer) => {
      this.map.removeLayer(layer);
    });
  }

  addRoutingControl(name, waypoints, options) {
    if (this.debug) console.log('addRoutingControl(name, waypoints, options) {')

    const controlOptions = {
      waypoints: waypoints,
      routeWhileDragging: options.hasOwnProperty('routeWhileDragging') ? options['routeWhileDragging'] : false,
      geocoder: options.hasOwnProperty('geocoder') ? options['geocoder'] : null,
      serviceUrl: options.hasOwnProperty('serviceUrl') ? options['serviceUrl'] : 'https://osrm.fretadao.com/route/v1',
      autoRoute: options.hasOwnProperty('autoRoute') ? options['autoRoute'] : true,
      collapsible: options.hasOwnProperty('collapsible') ? options['collapsible'] : true,
      show: options.hasOwnProperty('show') ? options['show'] : false,
      addWaypoints: options.hasOwnProperty('addWaypoints') ? options['addWaypoints'] : false,
      defaultErrorHandler: () => {
        console.warn('An error occurred when routing. The line will be rendered as a polyline as a fallback');

        this.clearAllPolylineLayers();

        if (!this.currentLine) return;

        this.#renderRouteForLine(
          this.currentLine,
          { lineColor: this.ACTIVE_COLOR, skipMarkers: true },
          this.currentDirection
        );
      }
    }

    if (options['createMarker'] !== undefined) controlOptions['createMarker'] = options['createMarker'];
    if (options['lineOptions'] !== undefined) controlOptions['lineOptions'] = options['lineOptions'];

    if (options.router) {
      if (options.router.tomtom && Object.keys(options.router.tomtom).length > 0) {
        controlOptions['router'] = new L.Routing.TomTom('oGbMiVjyaXTDbvOln4GgHhY3HJbIPfzr', options.router.tomtom);
      } else if (options.router.here && Object.keys(options.router.here).length > 0) {
        options.router.here.urlParameters = options.router.here.urlParameters || {};
        options.router.here.urlParameters.language = 'pt-BR';
        options.router.here.urlParameters.transportMode = options.router.here.urlParameters.transportMode || 'privateBus';
        options.router.here.urlParameters.return = 'polyline,summary,turnByTurnActions';
        controlOptions['router'] = new L.Routing.Here(
          'fF9nC32oD0LRXV2-EJ47XMaVqRyVnVGco6Vusbvg_Nc',
          options.router.here
        );
      }
    }

    this.routingControls[name] = L.Routing.control(controlOptions).addTo(this.map);

    // need to add some custom css to this work well
    // .leaflet-control-container .leaflet-routing-container-hide { display: none; }
    // https://github.com/perliedman/leaflet-routing-machine/issues/608#issuecomment-717680496
    if (this.mapOptions.hideItinerary === true) this.routingControls[name].hide();
  }

  createMarker(ll, options) {
    if (this.debug) console.log('createMarker(ll, options) {');

    const marker = L.marker(ll, {
      draggable: options['draggable'] ? options.draggable : false,
      contextmenu: true,
      contextmenuInheritItems: false,
      contextmenuItems: []
    });

    if (options.popup) marker.bindPopup(options.popup);
    if (options.icon) marker.options.icon = options.icon;
    if (options.alt) marker.options.alt = options.alt;
    if (options.zIndex) marker.options.forceZIndex = options.zIndex;
    if (options.hide !== undefined && options.hide) marker.options.opacity = 0;
    if (options.name) marker.options.name = options.name;

    return marker;
  }

  // ===================================================================================================================

  #addOverlay() {
    if (this.debug) console.log('#addOverlay() {');

    if (this.hasOverlayTarget) removeClass(this.overlayTarget, 'hidden');
  }

  #removeOverlay() {
    if (this.debug) console.log('#removeOverlay() {');

    if (this.hasOverlayTarget) addClass(this.overlayTarget, 'hidden');
  }

  async #getAllLines() {
    if (this.debug) console.log('async #getAllLines () {');

    this.lineIds = this.requestBaseCardTargets.map((elem) => {
      return elem.dataset.routeId;
    });

    await this.#fetchLines(this.lineIds).then(({ data }) => {
      data.lines.forEach((line) => {
        this.lines.push(this.#filterLineInformation(line));
      })
    }).catch(err => console.error(err.data))
  }

  #updateSelectedButton() {
    if (this.debug) console.log('#updateSelectedButton() {');
    if (this.#isIngoingDirection()) {
      this.outgoingButtonTargets.forEach((btn) => { removeClass(btn, 'bt--outlined-active') });
      this.ingoingButtonTargets.forEach((btn) => { addClass(btn, 'bt--outlined-active') });
    } else {
      this.ingoingButtonTargets.forEach((btn) => { removeClass(btn, 'bt--outlined-active') });
      this.outgoingButtonTargets.forEach((btn) => { addClass(btn, 'bt--outlined-active') });
    }
  }

  #setMapContainerHeight() {
    if (this.debug) console.log('#setMapContainerHeight() {')

    const hscreen = getParent(this.mapEl, 4);
    const navbar = find('nav', hscreen);
    const navbarStyle = navbar ? getComputedStyle(navbar) : null;
    const navbarHeight = navbarStyle ? (parseFloat(navbarStyle.height) + parseFloat(navbarStyle.marginTop) + parseFloat(navbarStyle.marginBottom)) : 0;
    const windowHeight = window.innerHeight;
    const mapContainer = getParent(this.mapEl, 3);

    mapContainer.style.height = `${parseFloat(windowHeight - navbarHeight)}px`;
    this.mapEl.style.height = `${parseFloat(windowHeight - navbarHeight)}px`;
  }

  #setMap() {
    if (this.debug) console.log('#setMap() {');
    if (isPresent(this.map)) return;

    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.prototype.options.iconUrl = iconImg;
    L.Icon.Default.prototype.options.shadowUrl = null;
    L.Icon.Default.prototype.options.iconRetinaUrl = iconRetinaImg;
    L.Icon.Default.prototype.options.shadowRetinaUrl = null;

    this.map = L.map(this.mapEl, {
      zoomControl: true,
      scrollWheelZoom: true,
      tap: false,
      touchZoom: true,
      doubleClickZoom: true,
      contextmenu: true,
    });

    this.map._lineLayers = {};

    this.markerPane = this.map.createPane('markerPane');
    this.markerPane.style.zIndex = 30000;

    if (this.hasOriginValue && this.originValue.length > 0) {
      this.map.setView(this.#formattedOriginLatLong(), this.INIT_ZOOM);
    } else {
      this.map.setView(this.INIT_COORDINATES, this.INIT_ZOOM);
    }

    this.map.options.minZoom = this.MIN_ZOOM;
    this.map.options.maxZoom = this.MAX_ZOOM;

    this.#setLayers();

    this.commonPane = this.map.createPane('commonPane');
    this.activePane = this.map.createPane('activePane');

    this.activePane.style.zIndex = 9999;

    if (this.hasOriginValue && this.mapOptions.showOrigin) this.#createOriginIcon();
    if (this.hasDestinationValue && this.mapOptions.showOrigin) this.#createDestinationIcon();

    this.#applyMapOptions();
  }

  #createOriginIcon() {
    if (this.debug) console.log('#createOriginIcon() {');

    this.originPane = this.map.createPane('originPane');
    this.originPane.style.zIndex = 31000;

    const icon = new L.Icon({
      iconUrl: originImg,
      iconSize: [40, 40],
      pane: 'originPane',
      zIndex: 11000
    });

    const marker = this.createMarker(this.#formattedOriginLatLong(), {
      pane: 'originPane',
      draggable: false,
      icon: icon,
      zIndex: 11000
    });

    marker.addTo(this.map);
  }

  #createDestinationIcon() {
    if (this.debug) console.log('#createDestinationIcon() {');

    this.destinationPane = this.map.createPane('destinationPane');
    this.destinationPane.style.zIndex = 31500;

    const icon = new L.DivIcon({
      className: 'text-center drop-shadow-lg',
      iconSize: [40, 40],
      pane: 'markerPane',
      html: `<img src="${iconEndPointCompany}" />`
    });

    const marker = this.createMarker(
      L.latLng(parseFloat(this.destinationValue[0]), parseFloat(this.destinationValue[1])),
      {
        pane: 'destinationPane',
        draggable: false,
        icon: icon,
        zIndex: 11500
      }
    );

    marker.addTo(this.map);
  }

  #formattedOriginLatLong() {
    if (this.debug) console.log('#formattedOriginLatLong() {');

    return L.latLng(parseFloat(this.originValue[0]), parseFloat(this.originValue[1]));
  }

  #setLayers() {
    if (this.debug) console.log('#setLayers() {');

    // Due Google Maps API the property styles needs to receive an Array.
    const roadMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'roadmap',
      styles: []
    });

    const satMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'satellite',
      styles: []
    });

    const hybridMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'hybrid',
      styles: []
    });

    const transitMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'roadmap',
      styles: []
    });

    const trafficMutant = L.gridLayer.googleMutant({
      maxZoom: this.MAX_ZOOM,
      type: 'roadmap',
      styles: []
    });

    transitMutant.addGoogleLayer('TransitLayer');
    trafficMutant.addGoogleLayer('TrafficLayer');

    const osm = L.tileLayer.provider('OpenStreetMap');
    const mapnik = L.tileLayer.provider('OpenStreetMap.Mapnik');

    const layers = {
      'Google Roadmap': roadMutant,
      'Google Roadmap Traffic': trafficMutant,
      'Google Roadmap Satellite': hybridMutant,
      'Google Transit': transitMutant,
      'Google Satellite': satMutant,
      'Mapnik': mapnik,
      'OpenStreetMaps': osm
    };

    if (['b2bTripDetailMap', 'b2bLineMap', 'addressForm', 'rideRequest'].includes(this.pageMethodValue)) {
      transitMutant.addTo(this.map);
    } else {
      osm.addTo(this.map);
    }

    this.control = L.control.layers(layers, {}).addTo(this.map);
  }

  #applyMapOptions() {
    if (this.debug) console.log('#applyMapOptions() {');

    if (isObject(this.mapOptions)) {
      if (isPresent(this.mapOptions.hideLayerControl)) this.control.remove();
      if (isPresent(this.mapOptions.layerControlPosition)) this.control.setPosition(this.mapOptions.layerControlPosition);
      if (isPresent(this.mapOptions.zoomPosition)) this.map.zoomControl.setPosition(this.mapOptions.zoomPosition);
      if (this.mapOptions.hideZoomControl) this.map.zoomControl.remove();
      if (isPresent(this.mapOptions.sidebar)) L.control.sidebar(this.mapOptions.sidebar).addTo(this.map);
      if (this.mapOptions.disableScrollZoom) this.map.scrollWheelZoom.disable();
    }
  }

  #b2bFetchLine(lineId) {
    if (this.debug) console.log('#fetchLine(lineId) {');

    return axios.get(`/b2b/api/lines/${lineId}`);
  }

  #b2bTripDetailFetchMap() {
    if (this.debug) console.log('#b2bTripDetailFechMap() {');

    return axios.get(`/b2b/api/${this.agreementSlugValue}/trip_details/${this.tripDetailIdValue}/line?direction=${this.directionValue}`);
  }

  #fetchLines(lineIds) {
    if (this.debug) console.log('#fetchLines(lineIds) {');

    return axios.get(`/api/lines/routes?line_ids=${lineIds}`);
  }

  #fetchCheckIns() {
    if (this.debug) console.log('#fetchCheckIns() {');

    API.b2bAdmin.tripDetailCheckIns(this.agreementSlugValue, this.tripDetailIdValue).then(
      this.#setCheckInsToPassengers.bind(this)
    );
  }

  async #passengerCheckins(){
    if (this.debug) console.log('#passengerCheckins(){');

    await this.#fetchCheckIns();
  }

  #setCheckInsToPassengers(response) {
    const checkIns = response.data['check_ins'];
    const direction = this.currentDirection;

    this.currentLine.passengers.forEach(passenger => {
      const associatedCheckIn = checkIns[direction].find(checkIn => checkIn.passenger_id == passenger.id);

      if (isPresent(associatedCheckIn)) {
        passenger[`${direction}_check_in_at`] = associatedCheckIn.registered_at;
      }
    });

    this.#addPassengersMarkers();
  }

  #filterLineInformation(line) {
    if (this.debug) console.log('#filterLineInformation(line) {');

    return {
      id: line.id,
      ingoing_waypoints: line.ingoing_waypoints.stop_all,
      outgoing_waypoints: line.outgoing_waypoints.stop_all,
      routing_engine: line.routing_engine,
      ingoing_vehicle: line.ingoing_vehicle,
      outgoing_vehicle: line.outgoing_vehicle,
      encoded_ingoing_polyline: line.encoded_ingoing_polyline,
      encoded_outgoing_polyline: line.encoded_outgoing_polyline,
      ingoing_route_geojson: line.ingoing_route_geojson,
      outgoing_route_geojson: line.outgoing_route_geojson,
    }
  }

  #getCardsWithLineId(lineId) {
    if (this.debug) console.log('#getCardsWithLineId(lineId) {');

    return this.lineCardTargets.filter((card) => card.dataset.routeId == lineId);
  }

  #applySelectedCardBorder(lineId) {
    if (this.debug) console.log('#applySelectedCardBorder(lineId) {');

    const selectedLineCards = this.#getCardsWithLineId(lineId);

    this.lineCardTargets.forEach((card) => removeClass(card, this.ACTIVE_LINE_CARD_CLASSES));
    selectedLineCards.forEach((card) => addClass(card, this.ACTIVE_LINE_CARD_CLASSES));
  }

  #createMarkerBasedOnDirection(line) {
    if (this.debug) console.log('#createMarkerBasedOnDirection(line) {');

    let validPassengers = this.currentLine?.passengers ? this.currentLine.passengers.filter((passenger) => passenger.latitude && passenger.longitude) : [];

    switch (this.pageMethodValue) {
      case 'b2bLineMap':
        if (this.readonlyValue) {
          // renders line as a polyline
          this.#addPassengers();
          this.#renderRouteForLine(line, { lineColor: this.ACTIVE_COLOR });
        } else {
          // calls lrm to render line
          this.#addRouteAndPassengerMarkers(line);
        }
        break;
      case 'b2bTripDetailMap':
        if (this.readonlyValue) {
          this.#addPassengers();
          // renders line as a polyline
          this.#renderRouteForLine(line, { lineColor: this.ACTIVE_COLOR });
        } else {
          // calls lrm to render line
          this.#addRouteAndPassengerMarkers(line);
        }

        this.#addLocationHistoryMarkers(line);
        break;
      default:
        if (this.readonlyValue) {
          this.#renderRouteForLine(line, {
            lineColor: line.id == this.selectedLine.id ? this.ACTIVE_COLOR : this.DISABLED_COLOR,
            zIndex: line.id == this.selectedLine.id ? this.ACTIVE_LINE_Z : this.DISABLED_LINE_Z,
            markerOptions: {
              lineId: line.id,
              createMarkerFunctiosn: this.#createSearchMarker.bind(this)
            }
          }, this.currentDirection);
        } else {
          if (this.#isIngoingDirection()) this.#addRouteMarkers(line, this.#createIngoingRouteMarker);
          else this.#addRouteMarkers(line, this.#createOutgoingRouteMarker);
        }
    }
  }

  #createSearchMarker(waypoint) {
    const icon = new L.Icon({
      iconUrl: this.#isCurrentAndActiveLinesTheSame() ? blueIcon : blackIcon,
      iconSize: [15, 15],
      pane: 'markerPane',
    });

    const marker = this.createMarker(waypoint.coords, {
      zIndexOffset: this.#isCurrentAndActiveLinesTheSame() ? 20000 : 10000,
      pane: 'markerPane',
      icon: icon,
      alt: waypoint,
      hide: waypoint.kind_point === "fake_stop"
    });

    let popup = '<span style="font-size: 11px;">' + waypoint.formatted_address + '</span><br/>';
    popup += '<span style="font-size: 11px">' + waypoint.schedule + '</span>';

    marker.bindPopup(popup);

    return marker;
  }

  #formatLineGeodata(lineData) {
    if (this.debug) console.log('#formatLineGeodata(lineData) {');

    return {
      line: lineData,
      waypoints: {
        ingoing: lineData.ingoing_waypoints_all || lineData.ingoing_waypoints,
        outgoing: lineData.outgoing_waypoints_all || lineData.outgoing_waypoints
      }
    }
  }

  #formatGeojson(geojson) {
    if (geojson?.geometry?.coordinates) {
      return geojson.geometry.coordinates.map((ll) => [ll[1], ll[0]]);
    } else {
      return [];
    }
  }

  #generatePolylineCoords(line, direction) {
    const encodedPolyline = line[`encoded_${direction}_polyline`];
    let coords = [];

    if (L.PolylineUtil != undefined && !!encodedPolyline) {
      coords = L.PolylineUtil.decode(encodedPolyline);
    } else {
      coords = this.#formatGeojson(line[`${direction}_route_geojson`]);
    }

    return coords;
  }

  #renderRouteForLine(line, options = {}, direction = this.directionValue) {
    if (this.debug) console.log('#renderRouteForLine(line) {');

    this.currentLine = line;

    const forceRefresh = options.force || false;
    const layerIdentifier = `${line.id}-${direction}`;

    if ((this.map._lineLayers[layerIdentifier] != undefined) && !forceRefresh) {
      this.map.addLayer(this.map._lineLayers[layerIdentifier]);

      return this.map._lineLayers[layerIdentifier];
    } else {
      const lineData = this.#formatLineGeodata(line);
      const polylineData = this.#generatePolylineCoords(lineData.line, direction);
      const polyline = L.polyline(polylineData, {
        interactive: false,
        smoothFactor: 1.5,
        stroke: true,
        color: options.lineColor || (direction === 'ingoing' ? 'green' : 'red')
      });
      const waypointMarkers = options.skipMarkers ? [] : this.#createWaypoints(lineData.waypoints[`${direction}`], options.markerOptions);
      const layerGroup = L.layerGroup([...waypointMarkers, polyline]);

      if (options.zIndex) layerGroup.setZIndex(options.zIndex);

      this.map._lineLayers[layerIdentifier] = layerGroup;

      layerGroup._mapBounds = L.latLngBounds(polylineData);
      layerGroup.addTo(this.map);

      return layerGroup;
    }
  }

  #createWaypoints(waypoints, options = {}) {
    if (this.debug) console.log('#createWaypoints(waypoints, options = {}) {');

    if (options.createMarkerFunction) {
      return waypoints.map((waypoint) => options.createMarkerFunction(waypoint));
    } else {
      return waypoints.map((waypoint, idx) => {
        let color = '';
        let icon = new L.DivIcon({
          className: 'bg-blue rounded-full text-white text-center border-white border-4 drop-shadow-lg',
          iconSize: [25, 25],
          pane: 'markerPane',
          html: waypoint.index
        });

        let popup = `<p style="font-size: 11px; font-weight: bold;">#${waypoint.index?.toString()?.padStart(2, '0')} - ${waypoint.schedule}</p>`;
        popup += `<p style="font-size: 11px;">${waypoint.formatted_address}</p>`;
        popup += `<p style="font-size: 11px; font-weight: bold;">EMBARQUE</p>`;

        if (waypoint.passengers) {
          const passengers = waypoint.passengers.map((passenger) => passenger.name).join('<br/>');
          popup += `<p style="font-size: 11px">${passengers}</p>`;
        }

        let marker = this.createMarker(waypoint.coords, {
          draggable: options.draggable || false,
          icon: options.icon || icon,
          keyboard: options.keyboard || true,
          title: options.title || waypoint.id,
          alt: options.alt || waypoint.index,
          zIndexOffset: options.zIndexOffset || 0,
          opacity: options.opacity || 1.0,
          riseOnHover: options.riseOnHover || false,
          riseOffset: options.riseOffset || 250,
          pane: options.pane || 'markerPane',
          shadowPane: options.shadowPane || 'shadowPane',
          bubblingMouseEvents: options.bubblingMouseEvents || false,
          autoPanOnFocus: options.autoPanOnFocus || true,
        });

        marker.waypoint = waypoint;
        marker.bindPopup(popup);
        marker.addTo(this.map);

        return marker;
      });
    }
  };

  #addLocationHistoryMarkers(line) {
    if (this.debug) console.log('#addLocationHistoryMarkers(line) {');

    this.currentLine = line;

    let histories = this.currentLine.location_history?.flatMap((locationData) => {
      const directions = Object.values(locationData)?.[0];
      const history =
        directions?.[this.directionValue]?.history ||
        directions?.roundtrip?.history ||
        [];
      return history;
    }) || [];

    histories.sort((a, b) => new Date(a.registered_at) - new Date(b.registered_at));

    // Apply font priority
    const sourcePriority = ['rastreauto', 'infleet', 'driver_app'];
    const selectedSource = sourcePriority.find((src) =>
      histories.some(h => h.source === src)
    );
    histories = histories.filter(h => h.source === selectedSource);

    const markerClusterGroup = L.markerClusterGroup({
      singleMarkerMode: true,
      maxClusterRadius: 25,
    });

    const polylinePoints = [];

    let lastPoint = null;

    histories.forEach((locationHistory) => {
      const { latitude, longitude, registered_at, speed } = locationHistory;
      const showOnMapUrl = `https://www.google.com/maps/place/${latitude},${longitude}`;
      const popup = `
        <p><strong>Horário:</strong> ${registered_at?.substr(11, 8)}h</p>
        <p><strong>Velocidade:</strong> ${speed?.toFixed(2)} KM/h</p>
        <p><strong>Lat,Lng:</strong> <a href="${showOnMapUrl}" target="_blank">${latitude},${longitude}</a></p>
      `;

      const marker = this.createMarker([latitude, longitude], {
        draggable: false,
        alt: locationHistory,
        popup: popup,
      });

      markerClusterGroup.addLayer(marker);

      // Adds to the route, as long as it is not more than 1600 meters from the previous one
      const currentPoint = L.latLng(latitude, longitude);
      if (!lastPoint || lastPoint.distanceTo(currentPoint) <= 1600) {
        polylinePoints.push([latitude, longitude]);
        lastPoint = currentPoint;
      }
    });

    const polyline = L.polyline(polylinePoints, {
      color: 'green',
      weight: 4,
      opacity: 0.7,
      pane: 'overlayPane',
    });

    polyline.addTo(this.map);
    this.map.addLayer(markerClusterGroup);
  }

  #addPassengersMarkers() {
    const hasCoordinate = (passenger) => passenger.latitude && passenger.longitude;
    const passengersWithCoordinates = this.currentLine.passengers.filter(hasCoordinate);

    const markers = passengersWithCoordinates.map((passenger) => {
      return this.#passengerMarker(passenger);
    });
    if (this.passengersGroup) this.passengersGroup.remove();
    this.passengersGroup = L.layerGroup(markers);
    this.passengersGroup.addTo(this.map);
  }

  #addPassengers() {
    if (this.debug) console.log('#addPassengers() {');
    this.waypoints = this.currentLine[`${this.currentDirection}_waypoints`];

    this.#handlePassengerAddition();

    const timeStringToMinutes = timeString => {
      const [hours, minutes] = timeString.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const currentMinutes = new Date().getHours() * 60 + new Date().getMinutes();
    const firstScheduleMinutes = timeStringToMinutes(this.waypoints[0].schedule);
    const lastScheduleMinutes = timeStringToMinutes(this.waypoints[this.waypoints.length - 1].schedule);

    if(firstScheduleMinutes <= currentMinutes && currentMinutes <= lastScheduleMinutes) {
        this.#scheduleNextPassengerUpdate();
    }
  }

  #handlePassengerAddition() {
    if (this.pageMethodValue === 'b2bTripDetailMap') {
      this.#passengerCheckins();
    } else {
      this.#addPassengersMarkers();
    }
  }

  #scheduleNextPassengerUpdate() {
    if (this.debug) console.log('#scheduleNextPassengerUpdate() {')
    setTimeout(() => {
      this.#addPassengers();
    }, 30000);
  }

  #addRouteAndPassengerMarkers(line) {
    if (this.debug) console.log('#addRouteAndPassengerMarkers(line) {');

    this.currentLine = line;

    this.#addPassengers()

    const routeMarkers = [];
    const routerOptions = { tomtom: {}, here: {} };

    let waypoints = this.#getLineWaypoints(line).filter((wp) => !wp._destroy);
    waypoints = sortBy(waypoints, waypoint => waypoint.index);
    waypoints.forEach((wp) => {
      const latLng = L.latLng(wp.latitude, wp.longitude);

      latLng._route = wp;
      routeMarkers.push(latLng);
    });

    if (line.routing_engine == "tomtom") {
      routerOptions.tomtom = { traffic: true, travelMode: "bus" };
    } else if (line.routing_engine == "here") {
      const vehicle = line[`${this.currentDirection}_vehicle`];
      const vehicleAllCharacteristics = vehicle ? vehicle.characteristics : undefined;
      const vehicleCharacteristics = vehicleAllCharacteristics ? vehicleAllCharacteristics.to_here_api : undefined;

      // TODO: "fix" request made because of https://fretadao.slack.com/archives/D0182BREFPC/p1734722152376589
      // routerOptions.here = {
      //   urlParameters: { vehicle: vehicleCharacteristics ? vehicleCharacteristics : null }
      // };
      routerOptions.here = { urlParameters: { vehicle: null } };
    }

    const lineColor = this.#isCurrentAndActiveLinesTheSame() ? this.ACTIVE_COLOR : this.DISABLED_COLOR;
    const controlName = `${this.currentDirection}-line#${line.id}`;
    const pane = this.#isCurrentAndActiveLinesTheSame() ? 'activePane' : 'commonPane';

    this.addRoutingControl(controlName, routeMarkers, {
      routeWhileDragging: (this.mapOptions.readonly === false),
      createMarker: (index, waypoint) => {
        let routes;
        let showCompany = false;
        let routeIndex = index;

        if (this.#isIngoingDirection()) {
          routes = this.#ingoingRoutes();
          showCompany = routes.length - 1 === index;
          routeIndex = index + 1;
        } else {
          routes = this.#outgoingRoutes();
          showCompany = index === 0;
        }

        const route = routes[index];
        if (!route) return;

        route.index = routeIndex;
        return this.#routeWithPassengerInfoMarker(route, waypoint, showCompany);
      },
      router: routerOptions,
      lineOptions: {
        styles: [{
          pane: pane,
          color: 'white',
          opacity: 0.8,
          weight: 6
        },
        {
          pane: pane,
          color: lineColor,
          opacity: 1,
          weight: 2
        }
        ]
      },
    });
  }

  #ingoingRoutes() {
    return this.currentLine.ingoing_waypoints.filter((route) => route.latitude && route.longitude);
  }

  #outgoingRoutes() {
    return this.currentLine.outgoing_waypoints.filter((route) => route.latitude && route.longitude);
  }

  #addRouteMarkers(line, markerBuildFunction) {
    if (this.debug) console.log('#addRouteMarkers(line, markerBuildFunction) {', line, markerBuildFunction);

    // so we can access the line inside the markerBuildFunction
    this.currentLine = line;

    const markers = [];
    const routerOptions = {
      tomtom: {},
      here: {}
    };

    let waypoints = this.#getLineWaypoints(line).filter((wp) => !wp._destroy);
    waypoints = sortBy(waypoints, waypoint => waypoint.index);

    waypoints.forEach((wp) => {
      const latLng = L.latLng(wp.latitude, wp.longitude);

      latLng._route = wp;
      markers.push(latLng);
    });

    if (line.routing_engine == "tomtom") routerOptions.tomtom = {
      traffic: true,
      travelMode: "bus"
    };
    else if (line.routing_engine == "here") routerOptions.here = {
      mode: "fastest;car",
      urlParameters: { language: 'pt-BR' }
    };

    const lineColor = this.#isCurrentAndActiveLinesTheSame() ? this.ACTIVE_COLOR : this.DISABLED_COLOR;
    const controlName = `${this.currentDirection}-line#${line.id}`;
    const pane = this.#isCurrentAndActiveLinesTheSame() ? 'activePane' : 'commonPane'

    this.addRoutingControl(controlName, markers, {
      routeWhileDragging: (this.mapOptions.readonly === false),
      createMarker: markerBuildFunction.bind(this),
      router: routerOptions,
      lineOptions: {
        styles: [{
          pane: pane,
          color: 'black',
          opacity: 0.15,
          weight: 9
        },
        {
          pane: pane,
          color: 'white',
          opacity: 0.8,
          weight: 6
        },
        {
          pane: pane,
          color: lineColor,
          opacity: 1,
          weight: 2
        }
        ]
      },
    });
  }

  #getRoutingControl(lineId, direction = undefined) {
    if (this.debug) console.log('#getRoutingControl(lineId, direction = undefined) {');
    if (direction != undefined) return this.routingControls[`${direction}-line#${lineId}`];

    return this.routingControls[`${this.currentDirection}-line#${lineId}`];
  }

  #createRouteMarker(route, waypoint, index) {
    if (this.debug) console.log('#createRouteMarker(route, waypoint, index) {');
    if (!route) return;

    route.index = index;

    return this.#defaultRouteMarker(route, waypoint);
  }

  #passengerMarker(passenger) {
    if (this.debug) console.log('#passengerMarker(passenger) {');

    const iconPath = passenger[`${this.currentDirection}_check_in_at`] ? iconCheckedInPassenger : iconPassenger;
    const icon = new L.DivIcon({
      className: 'text-center drop-shadow-lg',
      iconSize: [70, 70],
      pane: 'markerPassengerPane',
      html: `<img style="width: 100% !important;" src="${iconPath}" />`
    });

    let popup = this.#buildNameParagraphForPassenger(passenger);
    popup += `<p style="font-size: 11px;">${passenger.formatted_address}</p>`;
    if (passenger.ingoing_schedule_at) {
      popup += `<p style="font-size: 11px">Embarque na ida: ${passenger.ingoing_schedule_at}<br/>`;
      if (passenger.ingoing_check_in_at) {
        popup += `<p style="font-size: 11px">Horário do check-in na ida: ${passenger.ingoing_check_in_at}<br/>`;
      }
      if (isPresent(passenger.ingoing_walking_distance)) {
        popup += `Distância: ${passenger.ingoing_walking_distance} metros<br/>`;
      }
      popup += `${passenger.ingoing_address}</p>`;
    }

    if (passenger.outgoing_schedule_at) {
      popup += `<p style="font-size: 11px">Desembarque na volta: ${passenger.outgoing_schedule_at}<br/>`;
      if (passenger.outgoing_check_in_at) {
        popup += `<p style="font-size: 11px">Horário do check-in na volta: ${passenger.outgoing_check_in_at}<br/>`;
      }
      if (isPresent(passenger.outgoing_walking_distance)) {
        popup += `Distância: ${passenger.outgoing_walking_distance} metros<br/>`;
      }
      popup += `${passenger.outgoing_address}</p>`;
    }

    const marker = this.createMarker([passenger.latitude, passenger.longitude], {
      pane: 'markerPassengerPane',
      icon: icon,
      alt: passenger,
      zIndex: 100
    });

    marker.setZIndexOffset(999);
    marker.bindPopup(popup);
    this.passengersMarker[passenger.id] = marker;

    return marker;
  }

  #buildNameParagraphForPassenger(passenger) {
    if (this.debug) console.log('#buildNameParagraphForPassenger(passenger) {');

    let paragraphName = isPresent(passenger.pwd_kind) ? `<i class="fa-solid fa-wheelchair"></i> ${passenger.name}` : passenger.name;

    return `<p style="font-size: 11px; font-weight: bold;">${paragraphName}</p>`;
  }

  #routeWithPassengerInfoMarker(route, waypoint, company = false) {
    if (this.debug) console.log('#routeWithPassengerInfoMarker(route, waypoint, company) {');
    if (this.debug) console.log('route: ', route);

    if (company) {
      var icon = new L.DivIcon({
        className: 'text-center drop-shadow-lg',
        iconSize: [40, 40],
        pane: 'markerPane',
        html: `<img src="${iconEndPointCompany}" />`
      });

      var popup = '<p style="font-size: 11px;">' + route.formatted_address + '</p>';
      popup += '<p style="font-size: 11px">' + route.schedule + '</p>';
    } else {
      var icon = new L.DivIcon({
        className: 'bg-blue rounded-full text-white text-center border-white border-4 drop-shadow-lg',
        iconSize: [25, 25],
        pane: 'markerPane',
        html: route.index
      });

      var popup = `<p style="font-size: 11px; font-weight: bold;">#${route.index.toString().padStart(2, '0')} - ${route.schedule}</p>`;
      popup += `<p style="font-size: 11px;">${route.formatted_address}</p>`;
      popup += `<p style="font-size: 11px; font-weight: bold;">EMBARQUE</p>`;

      if (route.passengers) {
        const passengers = route.passengers.map((passenger) => passenger.name).join('<br/>');
        popup += `<p style="font-size: 11px">${passengers}</p>`;
      }
    }

    const marker = this.createMarker(waypoint.latLng, {
      pane: 'markerPane',
      icon: icon,
      alt: route,
      zIndex: 9000,
      hide: route.kind_point === "fake_stop"
    });

    marker.bindPopup(popup);

    return marker;
  }

  #defaultRouteMarker(route, waypoint) {
    if (this.debug) console.log('#defaultRouteMarker(route, waypoint) {');

    const icon = new L.Icon({
      iconUrl: this.#isCurrentAndActiveLinesTheSame() ? blueIcon : blackIcon,
      iconSize: [20, 20],
      pane: 'markerPane',
      zIndex: this.#isCurrentAndActiveLinesTheSame() ? this.ACTIVE_LINE_Z : this.DISABLED_LINE_Z
    });

    const marker = this.createMarker(waypoint.latLng, {
      pane: 'markerPane',
      icon: icon,
      alt: route,
      zIndex: this.#isCurrentAndActiveLinesTheSame() ? this.ACTIVE_LINE_Z : this.DISABLED_LINE_Z,
      hide: route.kind_point === "fake_stop"
    });

    let popup = '<span style="font-size: 11px;">' + route.formatted_address + '</span><br/>';
    popup += '<span style="font-size: 11px">' + route.schedule + '</span>';

    marker.bindPopup(popup);

    return marker;
  }

  #isCurrentAndActiveLinesTheSame() {
    if (this.debug) console.log('#isCurrentAndActiveLinesTheSame() {');

    return this.currentLine.id === this.selectedLine.id;
  }

  #createIngoingRouteMarker(index, waypoint) {
    if (this.debug) console.log('#createIngoingRouteMarker(index, waypoint) {');

    const route = this.#ingoingRoutes()[index];
    return this.#createRouteMarker(route, waypoint, index);
  }

  #createOutgoingRouteMarker(index, waypoint) {
    if (this.debug) console.log('#createOutgoingRouteMarker(index, waypoint) {');

    const route = this.#outgoingRoutes()[index];
    return this.#createRouteMarker(route, waypoint, index);
  }

  #getLineWaypoints(line, direction = this.currentDirection) {
    if (this.debug) console.log('#getLineWaypoints(line, direction = this.currentDirection) {');

    return line[`${direction}_waypoints`];
  }

  async #refreshLine(line) {
    if (this.debug) console.log('async #refreshLine(line) {');

    const control = this.#getRoutingControl(line.id);

    if (control != undefined) control.setWaypoints([]);

    await this.#createMarkerBasedOnDirection(line);
  }

  #searchCardForRouteId(el) {
    if (this.debug) console.log('#searchCardForRouteId(el) {');
    if (el.dataset.routeId != undefined) return el.dataset.routeId;

    return this.#searchCardForRouteId(getParent(el))
  }

  #isIngoingDirection() {
    if (this.debug) console.log('#isIngoingDirection() {');

    return this.currentDirection === 'ingoing';
  }
});
